import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { loadConfiguredProviders, type OAuthProviderId } from '@/features/auth/services/oauthService.http'

/**
 * The SSO providers this environment offers (CR-SSO-INVITE-PX-2026-09-26).
 *
 * One read per app load, shared by `/login` and `/accept-invite`. The list
 * FAILS CLOSED: while it loads, when it fails, and when nothing is configured,
 * `selectOfferedOAuthProviders` answers an empty list and no SSO block renders
 * at all — a button that can only fail is worse than a missing one (AC-8,
 * AC-11). GitHub is never offered, even when the server lists it.
 */
export interface OAuthProvidersState {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  offered: OAuthProviderId[]
}

const initialState: OAuthProvidersState = { status: 'idle', offered: [] }

/**
 * Load the configured providers once. The `condition` reads the live store, so
 * a StrictMode double-invoked effect, or both pages mounting it, reads exactly
 * once per app load.
 */
export const loadOAuthProviders = createAsyncThunk(
  'oauthProviders/load',
  async () => loadConfiguredProviders(),
  {
    condition: (_arg, { getState }) =>
      (getState() as { oauthProviders: OAuthProvidersState }).oauthProviders.status === 'idle'
  }
)

export const oauthProvidersSlice = createSlice({
  name: 'oauthProviders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadOAuthProviders.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(loadOAuthProviders.fulfilled, (state, action) => {
        state.status = 'loaded'
        state.offered = action.payload
      })
      .addCase(loadOAuthProviders.rejected, (state) => {
        state.status = 'failed'
        state.offered = []
      })
  }
})

/** One module constant, so an empty answer is the same reference every call. */
const NONE: readonly OAuthProviderId[] = []

/** The providers to render a button for — empty until the list has loaded. */
export const selectOfferedOAuthProviders = (s: {
  oauthProviders: OAuthProvidersState
}): readonly OAuthProviderId[] => (s.oauthProviders.status === 'loaded' ? s.oauthProviders.offered : NONE)
